import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, X, Check, Target, Activity, Dumbbell, Zap,
} from "lucide-react";
import { joinProgramWaitlist, getReenroll } from "@/lib/checkout";
import { getCategories } from "@/lib/training";
import CategoryCards from "@/components/training/CategoryCards";
import { WhyTrainHere, MeetCoach, TrainingLocation } from "@/components/HomeSections";
import { LEGAL_LINKS } from "@/lib/legal";
import { LeaderboardPreview } from "@/components/Leaderboard";
import { track } from "@/lib/track";
import { fmtShortDate } from "@/lib/dates";

const HERO_IMG =
  "https://images.unsplash.com/photo-1633809786904-90d4c03ca502?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const PHILO_IMG =
  "https://images.unsplash.com/photo-1686256945090-7f4661e5eb1d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const LOGO =
  "https://customer-assets.emergentagent.com/job_data-driven-baseball/artifacts/94x88gnl_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2003_59_37%20PM.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function FadeUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} transition={{ delay }} className={className}>
      {children}
    </motion.div>
  );
}

function Overline({ children }) {
  return <p className="text-sm font-bold tracking-[0.25em] uppercase text-[#2563EB]">{children}</p>;
}

const PROGRAM_META = {
  hitting: { icon: Target, label: "Rep Lab Hitting", blurb: "Purposeful hitting development built around quality repetitions, approach, adjustments, and competition." },
  pitching: { icon: Activity, label: "Rep Lab Pitching", blurb: "Structured throwing and pitching development focused on movement, command, intent, and competing in the strike zone." },
  strength: { icon: Dumbbell, label: "Rep Lab Strength", blurb: "Age-appropriate strength and movement training designed to build stronger, more athletic baseball players." },
  speed: { icon: Zap, label: "Rep Lab Speed", blurb: "Speed, agility, acceleration, and movement training designed to improve how athletes move on the field." },
};
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function Nav({ onViewTraining }) {
  return (
    <header data-testid="main-nav" className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-16 h-20 md:h-24 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); scrollTo("top"); }} className="flex items-center">
          <img src={LOGO} alt="Rep Lab" className="h-14 md:h-20 w-auto object-contain" />
        </a>
        <div className="flex items-center gap-4 md:gap-6">
        <Link to="/leaderboard" data-testid="nav-leaderboard-link" className="text-sm font-bold text-[#0B0F19] hover:text-[#2563EB] transition-colors">Leaderboard</Link>
        <button data-testid="nav-view-training-btn" onClick={onViewTraining} className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-sm font-bold px-6 py-3 rounded-full transition-colors duration-200">
          View Training
        </button>
        </div>
      </div>
    </header>
  );
}

function Hero({ onViewTraining }) {
  return (
    <section id="top" className="bg-white pt-20 md:pt-28">
      <div className="max-w-7xl mx-auto px-6 md:px-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center pb-10 md:pb-14 pt-6 md:pt-10">
        <div>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-xs md:text-sm font-bold tracking-[0.3em] uppercase text-[#2563EB]">
            Rep Lab Baseball
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.65 }} className="mt-4 text-[40px] leading-[0.95] sm:text-6xl font-extrabold tracking-tighter uppercase text-[#0B0F19] leading-[0.92]">
            Don't just chase<br />the results.<br /><span className="text-[#2563EB]">Train for them.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28, duration: 0.55 }} className="mt-8 text-sm md:text-base font-bold tracking-[0.2em] uppercase text-[#0B0F19]/45">
            Hitting · Pitching · Strength · Speed
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.38, duration: 0.55 }}>
            <button data-testid="hero-view-training-btn" onClick={onViewTraining} className="mt-7 group inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-base md:text-lg font-bold px-10 py-5 rounded-full transition-all duration-200 hover:scale-[1.02]">
              View Training <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: "easeOut" }} className="relative hidden sm:block">
          <div className="aspect-[4/3] lg:aspect-[5/4] max-h-[440px] w-full overflow-hidden rounded-3xl bg-slate-100">
            <img src={HERO_IMG} alt="Athlete training in the cage" className="w-full h-full object-cover" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Philosophy() {
  return (
    <section className="bg-white py-20 md:py-28 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6 md:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="order-2 lg:order-1">
          <div className="aspect-[5/4] w-full overflow-hidden rounded-3xl bg-slate-100">
            <img src={PHILO_IMG} alt="Coach working with athletes" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="order-1 lg:order-2 max-w-xl">
          <FadeUp><Overline>Our Philosophy</Overline></FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="mt-5 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter uppercase text-[#0B0F19] leading-[0.95]">Development has to be earned.</h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-7 space-y-4 text-base md:text-lg font-medium text-slate-500 leading-relaxed">
              <p>Baseball has a way of exposing what you're willing to work for.</p>
              <p>At Rep Lab, we teach athletes how to train with purpose, compete, handle failure, make adjustments, and take ownership of their development.</p>
              <p>Our coaches provide direction, feedback, and an environment with high standards — but the athlete has to do the work. We're not here to create carbon copies or provide every answer. We're here to teach players how to get better.</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.14}>
            <p className="mt-9 text-xl sm:text-2xl font-extrabold tracking-tight uppercase text-[#0B0F19] leading-tight">
              You don't have to be the best player in the room. <span className="text-[#2563EB]">You have to want to get better.</span>
            </p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

const STATUS_BADGE = {
  open: { label: "Available", cls: "bg-emerald-500/20 text-emerald-300" },
  limited: { label: "Limited Spots", cls: "bg-amber-500/20 text-amber-300" },
  full: { label: "Full", cls: "bg-red-500/20 text-red-300" },
  coming_soon: { label: "Coming Soon", cls: "bg-white/10 text-white/60" },
};

function fmtTime(start, end) {
  const norm = (t) => (t || "").replace(/\s*([ap])\.?m\.?/i, (_, x) => " " + x.toUpperCase() + "M").replace(/\s+/g, " ").trim();
  const s = norm(start);
  const e = norm(end);
  return e ? `${s} – ${e}` : s;
}


export function ProgramCard({ p, onRegister, onWaitlist, index }) {
  const meta = PROGRAM_META[p.program_type] || PROGRAM_META.hitting;
  const badge = STATUS_BADGE[p.display_status] || STATUS_BADGE.open;
  const isOpen = p.display_status === "open" || p.display_status === "limited";
  return (
    <FadeUp delay={index * 0.06}>
      <div data-testid={`training-card-${p.id}`} className="rounded-3xl border border-white/10 bg-white/5 p-7 h-full flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-[#60a5fa]">
            <meta.icon size={16} /> {p.type_label}
          </span>
          <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold uppercase ${badge.cls}`}>{badge.label}</span>
        </div>
        <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-white">{p.title}</h3>
        {p.day && <span className="mt-2 inline-block rounded-full bg-[#2563EB]/15 border border-[#2563EB]/30 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#60a5fa]">{p.day}{p.time ? ` · ${p.time}` : ""}</span>}
        {p.description && <p className="mt-4 text-sm font-medium text-white/50 leading-relaxed">{p.description}</p>}

        <div className="mt-6 grid grid-cols-2 gap-y-3 gap-x-4 text-sm border-t border-white/10 pt-5">
          {p.day && <Info label="Day" value={p.day} />}
          {p.time && <Info label="Time" value={fmtTime(p.time, p.end_time)} />}
          {(p.start_date || p.end_date) && <Info label="Dates" value={[p.start_date, p.end_date].filter(Boolean).map(fmtShortDate).join(" – ")} />}
          <Info label="Cycle" value={`${p.sessions_count} Sessions / ${p.program_length || `${p.sessions_count} Weeks`}`} />
          <Info label="Group Size" value={`${p.capacity} Max`} />
          {p.coach && <Info label="Coach" value={p.coach} />}
          {p.location && <Info label="Location" value={p.location} />}
        </div>

        <div className="mt-6 flex items-end justify-between gap-4 border-t border-white/10 pt-5">
          <div>
            <p className="text-3xl font-extrabold tracking-tighter text-white">${p.price}</p>
            {isOpen && <p className="mt-1 text-xs font-bold text-[#60a5fa]">{p.remaining} {p.remaining === 1 ? "Spot" : "Spots"} Available</p>}
          </div>
          {isOpen ? (
            <button data-testid={`training-register-${p.id}`} onClick={() => onRegister(p)} className="group inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] text-white text-sm font-bold px-6 py-3 rounded-full transition-colors">
              Register <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          ) : (
            <button data-testid={`training-waitlist-${p.id}`} onClick={() => onWaitlist(p)} className="inline-flex items-center gap-2 border border-white/20 text-white text-sm font-bold px-6 py-3 rounded-full hover:bg-white/10 transition-colors">
              Join Waitlist
            </button>
          )}
        </div>
      </div>
    </FadeUp>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wider text-white/35">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-white/80">{value}</p>
    </div>
  );
}

const inputCls = "w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder-white/30 outline-none focus:border-[#2563EB] transition-colors";

export function WaitlistModal({ program, onClose }) {
  const [form, setForm] = useState({ athlete_name: "", parent_name: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const e = {};
    if (!form.athlete_name.trim()) e.athlete_name = "Required";
    if (!form.parent_name.trim()) e.parent_name = "Required";
    if (!form.email || !/.+@.+\..+/.test(form.email)) e.email = "Valid email required";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      await joinProgramWaitlist({ program_id: program.id, ...form });
      setDone(true);
    } catch (err) {
      setErrors({ submit: err?.response?.data?.detail || "Something went wrong." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={() => !submitting && onClose()} />
      <div data-testid="waitlist-modal" className="relative w-full max-w-md my-8 bg-[#0B0F19] border border-white/10 rounded-3xl p-7 md:p-9">
        <button onClick={onClose} className="absolute top-5 right-5 text-white/50 hover:text-white"><X size={22} /></button>
        {done ? (
          <div data-testid="waitlist-modal-success" className="text-center py-4">
            <div className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#2563EB]/15 text-[#2563EB]"><Check size={28} strokeWidth={3} /></div>
            <h2 className="mt-6 text-2xl font-extrabold tracking-tight text-white">You're on the list.</h2>
            <p className="mt-3 text-sm font-medium text-white/60">We'll email you the moment a spot opens in {program.title}.</p>
            <button onClick={onClose} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#60a5fa]">Done</button>
          </div>
        ) : (
          <>
            <Overline>Join Waitlist</Overline>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight text-white">{program.title}</h2>
            <p className="mt-2 text-sm font-medium text-white/50">{[program.day, program.time].filter(Boolean).join(" · ")} — we'll notify you when a spot opens.</p>
            <div className="mt-6 space-y-3">
              <div>
                <input data-testid="wl-athlete-name" value={form.athlete_name} onChange={(e) => set("athlete_name", e.target.value)} placeholder="Athlete Name *" className={`${inputCls} ${errors.athlete_name ? "border-red-400/60" : ""}`} />
                {errors.athlete_name && <span className="mt-1 block text-xs font-semibold text-red-400">{errors.athlete_name}</span>}
              </div>
              <div>
                <input data-testid="wl-parent-name" value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} placeholder="Parent / Guardian Name *" className={`${inputCls} ${errors.parent_name ? "border-red-400/60" : ""}`} />
                {errors.parent_name && <span className="mt-1 block text-xs font-semibold text-red-400">{errors.parent_name}</span>}
              </div>
              <div>
                <input data-testid="wl-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className={`${inputCls} ${errors.email ? "border-red-400/60" : ""}`} />
                {errors.email && <span className="mt-1 block text-xs font-semibold text-red-400">{errors.email}</span>}
              </div>
              <input data-testid="wl-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" className={inputCls} />
            </div>
            {errors.submit && <p className="mt-4 text-sm font-semibold text-red-400">{errors.submit}</p>}
            <button data-testid="wl-submit-btn" onClick={submit} disabled={submitting} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1d4fd7] disabled:opacity-50 text-white font-bold px-6 py-4 rounded-full transition-colors">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : "Join Waitlist"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function LegalFooterLinks() {
  return (
    <div data-testid="footer-legal-links" className="flex flex-wrap items-center justify-center md:justify-end gap-x-4 gap-y-1.5">
      {LEGAL_LINKS.map((l) => (
        <Link key={l.slug} to={`/legal/${l.slug}`} data-testid={`footer-legal-${l.slug}`} className="text-xs font-semibold text-white/40 hover:text-white transition-colors">{l.label}</Link>
      ))}
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0B0F19] border-t border-white/5 py-14">
      <div className="max-w-7xl mx-auto px-6 md:px-16 flex flex-col md:flex-row items-center justify-between gap-8">
        <img src={LOGO} alt="Rep Lab" className="h-16 w-auto object-contain mix-blend-screen" />
        <div className="flex flex-col items-center md:items-end gap-3">
          <div className="flex items-center gap-6">
            <button data-testid="footer-programs-link" onClick={() => scrollTo("training")} className="text-sm font-semibold text-white/60 hover:text-white transition-colors">Training</button>
            <a href="mailto:info@replabbaseball.com" data-testid="footer-contact-email" className="text-sm font-semibold text-white/60 hover:text-white transition-colors">Contact</a>
          </div>
          <LegalFooterLinks />
          <p className="text-sm font-medium text-white/40">© {new Date().getFullYear()} Rep Lab. Don't just chase the results.</p>
        </div>
      </div>
    </footer>
  );
}

function StickyMobileCta({ onViewTraining }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!show) return null;
  return (
    <div className="md:hidden fixed bottom-0 inset-x-0 z-40 p-3 bg-[#0B0F19]/95 backdrop-blur-md border-t border-white/10">
      <button data-testid="sticky-mobile-cta" onClick={onViewTraining} className="w-full inline-flex items-center justify-center gap-2 bg-[#2563EB] text-white text-base font-bold px-6 py-4 rounded-full">
        View Training <ArrowRight size={18} />
      </button>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reenrollNotice, setReenrollNotice] = useState("");

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("reenroll");
    if (token) {
      getReenroll(token).then((re) => {
        const t = re.next_program?.program_type;
        if (t) navigate(`/training/${t}?offering=${re.next_program_id}&reenroll=${encodeURIComponent(token)}`, { replace: true });
        else setReenrollNotice("The next cycle is no longer available. See current training below.");
      }).catch((e) => setReenrollNotice(e?.response?.data?.detail || "This link is no longer valid."));
    }
    getCategories().then(setCategories).finally(() => {
      setLoading(false);
      if (window.location.hash === "#training" || window.location.hash === "#current-training") setTimeout(() => scrollTo("training"), 200);
    });
  }, [navigate]);

  const viewTraining = () => scrollTo("training");
  useEffect(() => { track("home_view", "home"); }, []);
  return (
    <div className="bg-white">
      <Nav onViewTraining={viewTraining} />
      <Hero onViewTraining={viewTraining} />
      <LeaderboardPreview />
      <CategoryCards categories={categories} loading={loading} />
      <WhyTrainHere onViewTraining={viewTraining} />
      <MeetCoach />
      <Philosophy />
      <TrainingLocation />
      <Footer />
      <StickyMobileCta onViewTraining={viewTraining} />
      {reenrollNotice && (
        <div data-testid="reenroll-notice" className="fixed top-24 inset-x-4 z-[60] mx-auto max-w-lg rounded-2xl bg-[#0B0F19] border border-white/15 px-5 py-4 text-sm font-semibold text-white shadow-xl flex items-start justify-between gap-4">
          <span>{reenrollNotice}</span>
          <button data-testid="reenroll-notice-close" onClick={() => setReenrollNotice("")} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
      )}
    </div>
  );
}
